import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

const EMPTY_QUEUE_COUNTS = {
  needs_review: 0,
  active: 0,
  later: 0,
  resolved_today: 0,
};

const EMPTY_QUEUE_STATE = {
  day_key: '',
  timezone: 'America/New_York',
  reset_hour_local: 5,
  needs_review: [],
  active: [],
  later: [],
  resolved_today: [],
  counts: EMPTY_QUEUE_COUNTS,
};

async function parseErrorDetail(response) {
  const raw = await response.text().catch(() => '');
  if (!raw) {
    return response.statusText || 'Request failed';
  }

  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed?.detail === 'string' && parsed.detail.trim()) {
      return parsed.detail.trim();
    }
  } catch (_) {
    // Fall through to raw text.
  }

  return raw;
}

export function useCommandCenterQueueState({
  activeTab,
  adminFetch,
  hasToken,
}) {
  const [queueState, setQueueState] = useState(EMPTY_QUEUE_STATE);
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueRefreshing, setQueueRefreshing] = useState(false);
  const [queueError, setQueueError] = useState('');
  const [creatingQueueItem, setCreatingQueueItem] = useState(false);
  const [mutatingIds, setMutatingIds] = useState({});

  const fetchQueue = useCallback(async ({ silent = false } = {}) => {
    if (!hasToken) {
      setQueueLoading(false);
      return;
    }

    if (silent) {
      setQueueRefreshing(true);
    } else {
      setQueueLoading(true);
    }

    try {
      const response = await adminFetch('/admin/command-center/queue');
      if (!response.ok) {
        const detail = await parseErrorDetail(response);
        throw new Error(detail || 'Failed to load Command Center queue');
      }

      const data = await response.json();
      setQueueState({
        ...EMPTY_QUEUE_STATE,
        ...data,
        counts: {
          ...EMPTY_QUEUE_COUNTS,
          ...(data?.counts || {}),
        },
        needs_review: Array.isArray(data?.needs_review) ? data.needs_review : [],
        active: Array.isArray(data?.active) ? data.active : [],
        later: Array.isArray(data?.later) ? data.later : [],
        resolved_today: Array.isArray(data?.resolved_today) ? data.resolved_today : [],
      });
      setQueueError('');
    } catch (error) {
      const message = error?.message || 'Failed to load Command Center queue';
      setQueueError(message);
      if (!silent) {
        toast.error(message);
      }
    } finally {
      setQueueLoading(false);
      setQueueRefreshing(false);
    }
  }, [adminFetch, hasToken]);

  const runMutation = useCallback(async ({
    queueItemId,
    endpoint,
    method = 'POST',
    body,
    successMessage,
    failureMessage,
  }) => {
    if (!queueItemId) return null;

    setMutatingIds((current) => ({ ...current, [queueItemId]: true }));
    try {
      const response = await adminFetch(endpoint, {
        method,
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!response.ok) {
        const detail = await parseErrorDetail(response);
        throw new Error(detail || failureMessage || 'Queue update failed');
      }

      const data = await response.json().catch(() => null);
      if (successMessage) {
        toast.success(successMessage);
      }
      await fetchQueue({ silent: true });
      return data;
    } catch (error) {
      toast.error(error?.message || failureMessage || 'Queue update failed');
      return null;
    } finally {
      setMutatingIds((current) => {
        const next = { ...current };
        delete next[queueItemId];
        return next;
      });
    }
  }, [adminFetch, fetchQueue]);

  const resolveQueueItem = useCallback((queueItemId) => (
    runMutation({
      queueItemId,
      endpoint: `/admin/command-center/queue/${queueItemId}/resolve`,
      successMessage: 'Action marked resolved',
      failureMessage: 'Failed to resolve action',
    })
  ), [runMutation]);

  const deferQueueItem = useCallback((queueItemId) => (
    runMutation({
      queueItemId,
      endpoint: `/admin/command-center/queue/${queueItemId}/defer`,
      successMessage: 'Action moved to later',
      failureMessage: 'Failed to defer action',
    })
  ), [runMutation]);

  const reviewQueueItem = useCallback((queueItemId, payload) => (
    runMutation({
      queueItemId,
      endpoint: `/admin/command-center/queue/${queueItemId}/review`,
      body: payload,
      successMessage: payload?.state === 'later' ? 'Review item moved to later' : 'Review item promoted to active',
      failureMessage: 'Failed to review item',
    })
  ), [runMutation]);

  const dismissQueueItem = useCallback((queueItemId) => (
    runMutation({
      queueItemId,
      endpoint: `/admin/command-center/queue/${queueItemId}/dismiss`,
      successMessage: 'Review item dismissed',
      failureMessage: 'Failed to dismiss review item',
    })
  ), [runMutation]);

  const updateQueueItem = useCallback((queueItemId, payload) => (
    runMutation({
      queueItemId,
      endpoint: `/admin/command-center/queue/${queueItemId}`,
      method: 'PATCH',
      body: payload,
      successMessage: 'Queue item updated',
      failureMessage: 'Failed to update queue item',
    })
  ), [runMutation]);

  const deleteQueueItem = useCallback((queueItemId) => (
    runMutation({
      queueItemId,
      endpoint: `/admin/command-center/queue/${queueItemId}`,
      method: 'DELETE',
      successMessage: 'Queue item deleted',
      failureMessage: 'Failed to delete queue item',
    })
  ), [runMutation]);

  const createQueueItem = useCallback(async (payload) => {
    setCreatingQueueItem(true);
    try {
      const response = await adminFetch('/admin/command-center/queue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const detail = await parseErrorDetail(response);
        throw new Error(detail || 'Failed to add queue item');
      }

      const data = await response.json();
      toast.success('Queue item added');
      await fetchQueue({ silent: true });
      return data;
    } catch (error) {
      toast.error(error?.message || 'Failed to add queue item');
      return null;
    } finally {
      setCreatingQueueItem(false);
    }
  }, [adminFetch, fetchQueue]);

  useEffect(() => {
    if (activeTab !== 'overview') return;
    fetchQueue();
  }, [activeTab, fetchQueue]);

  const queueActions = useMemo(() => ({
    refreshQueue: () => fetchQueue({ silent: true }),
    createQueueItem,
    resolveQueueItem,
    deferQueueItem,
    reviewQueueItem,
    dismissQueueItem,
    updateQueueItem,
    deleteQueueItem,
  }), [createQueueItem, deferQueueItem, dismissQueueItem, fetchQueue, resolveQueueItem, reviewQueueItem, updateQueueItem, deleteQueueItem]);

  return {
    queueState,
    queueLoading,
    queueRefreshing,
    queueError,
    creatingQueueItem,
    mutatingIds,
    ...queueActions,
  };
}
